/* override functions api.jquery.js */

/*
 * Cart UI refresh
 * --------------------------------------------------------------------------
 * The visible "Add to Cart" button on product pages is rendered by the
 * product-customizer app (#tcustomizer-add), which POSTs to /cart/add.js
 * itself and never calls the theme's callbacks. The theme's own #AddToCart
 * is hidden underneath it. That is why the header badge and mini-cart used
 * to stay stale after an add.
 *
 * So instead of refreshing from inside addItemFromForm only, we watch every
 * request to a cart-mutating endpoint (whoever makes it) and refresh once.
 * The refresh is debounced, so several triggers in the same moment collapse
 * into a single /cart.js call rather than the four we used to fire.
 */
Shopify.cartRefreshDelay = 120;

(function () {
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var CART_ADD      = /\/cart\/add(\.js|\.json)?(\?|$)/;
  var CART_READ     = /\/cart(\.js|\.json)(\?|$)/;
  var timer = null;

  /*
   * Read-through cache for GET /cart.js
   * --------------------------------------------------------------------
   * The EA Sticky Cart app (which renders the cart drawer) re-fetches
   * /cart.js every 2 seconds for as long as a page stays open - about 30
   * requests a minute, forever, cancelling each one it has not received by
   * the time the next tick fires. DevTools blames cart.api.js for them only
   * because this file wraps window.fetch; the poll is the app's own.
   *
   * The cart cannot change without a cart mutation, and every mutation
   * already passes through the wrappers below, so between mutations the
   * answer is known and the network round trip is pure waste. Serve a
   * cached copy instead, dropped the moment anything mutates the cart and
   * expired on a timer anyway so a change made in another tab still lands.
   */
  var CART_CACHE_TTL = 10000;
  var cartCache = null;   // { at: <ms>, body: <string> }
  var cartInFlight = null;

  function invalidateCartCache() {
    cartCache = null;
  }

  // Cache only a plain GET - a Request object or any method with a body is
  // somebody else's business and is passed straight through.
  function isCacheableCartRead(input, init) {
    if (typeof Response !== 'function') return false;
    if (typeof input !== 'string') return false;
    if (!CART_READ.test(input)) return false;
    if (!init) return true;
    if (init.body != null) return false;
    return !init.method || String(init.method).toUpperCase() === 'GET';
  }

  function cachedCartResponse() {
    return new Response(cartCache.body, {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function matchesUrl(re, url) {
    if (!url) return false;
    try {
      return re.test(String(url));
    } catch (e) {
      return false;
    }
  }

  function isCartMutation(url) { return matchesUrl(CART_MUTATION, url); }
  function isCartAdd(url)      { return matchesUrl(CART_ADD, url); }

  // Exposed so other scripts can ask for a refresh without knowing the details.
  Shopify.refreshCartUI = function () {
    if (timer) return; // one refresh already queued
    timer = setTimeout(function () {
      timer = null;
      if (typeof Shopify.getCart === 'function') {
        Shopify.getCart();
      }
    }, Shopify.cartRefreshDelay);
  };

  /*
   * Theme-owned line item properties
   * --------------------------------------------------------------------
   * On customizable products the visible Add to Cart button belongs to the
   * option-set app. The theme's #AddToCart only clicks it and returns (see
   * snippets/product-cart-action.liquid), so the app builds the
   * /cart/add.js payload from its own fields and never sees the
   * properties[...] inputs the theme renders. The Custom Note textarea and
   * the measurement confirmation were dropped on every such product.
   *
   * Rather than fight the app for ownership of the button, we merge the
   * theme's own property inputs into the outgoing add request, whoever
   * makes it. Keys already present are never overwritten, so the theme's
   * own form post (non-app products) is unaffected.
   *
   * The Custom Note block is deliberately NOT in this list: it carries no
   * properties[...] name at all and is written to the ORDER NOTE instead
   * (see syncOrderNote below), so it lands in the admin's Notes panel and
   * the standard "Note" export column rather than becoming a second
   * per-line-item column.
   */
  /*
   * #lk-sheet is scoped alongside #AddToCartForm because the mobile size
   * sheet MOVES .door-measurement-confirm and .product-cart-action into
   * itself (see snippets/custom-dimension-visualizer.liquid). They keep a
   * form="AddToCartForm" association but are no longer DOM descendants of
   * the form, so a form-only selector stops matching while it is open.
   */
  var THEME_PROPERTY_FIELDS = [
    '#AddToCartForm [name^="properties["]',
    '#lk-sheet [name^="properties["]',
    '#lk-cx-fields [name^="properties["]'
  ].join(', ');

  /*
   * Order note
   * --------------------------------------------------------------------
   * Fields marked data-lk-order-note are collected at click time (the app
   * can take seconds to POST, by which point the arm window may have shut)
   * and pushed to /cart/update.js once the add succeeds. cart.note is what
   * the admin shows under "Notes" and what the Note column of an order
   * export contains.
   */
  var ORDER_NOTE_FIELDS = '#AddToCartForm [data-lk-order-note], #lk-sheet [data-lk-order-note], [data-lk-order-note], .special-guide-note__textarea, .special-guide-note__input, textarea[name*="note" i], input[name*="note" i], textarea[name="properties[Custom Note]"]';
  var armedNote = '';

  function collectOrderNote() {
    var nodes = document.querySelectorAll(ORDER_NOTE_FIELDS);
    var parts = [];

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.disabled) continue;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue;

      var value = (el.value == null ? '' : String(el.value)).trim();
      if (!value) continue;

      var label = el.getAttribute('data-lk-order-note') || '';
      if (!label && el.name) {
        var match = /^properties\[(.*)\]$/.exec(el.name);
        label = match ? match[1] : '';
      }

      parts.push({ label: label, value: value });
    }

    if (!parts.length) return '';

    // A single note reads best as the customer's own words, with nothing
    // bolted on. Only label the lines when there is more than one field.
    if (parts.length === 1) return parts[0].value;

    return parts.map(function (p) {
      return p.label ? p.label + ': ' + p.value : p.value;
    }).join('\n');
  }

  /*
   * #AddToCartForm also holds the complementary/related product widgets,
   * which add OTHER products to the cart, and quick view posts a form of
   * its own. Merging on every add would staple a customer's door note onto
   * an unrelated line item, so we only merge while an add that actually
   * originated from this product's own controls is in flight.
   */
  var APP_BUTTONS = '.add-to-cart-container button, .addToCartCustomButton, #tcustomizer-add, .tcustomizer-overlap-button, .tcustomizer__submit-button, .tcustomizer__btn-add, product-options-section .custom-cart-btn';
  var OTHER_PRODUCT = '.product-complementary, .complementary_releted_product_area, .product-grid-item';
  var ARM_WINDOW = 30000;
  var armedUntil = 0;

  function doArmCart(target) {
    var el = (target && target.nodeType === 1) ? target : (target && target.parentElement ? target.parentElement : null);
    if (el && typeof el.closest === 'function') {
      if (el.closest(OTHER_PRODUCT)) return;
    }
    var form = document.getElementById('AddToCartForm');
    if (!el || (form && form.contains(el)) || (el && typeof el.closest === 'function' && (el.closest(APP_BUTTONS) || el.closest('#lk-sheet')))) {
      armedUntil = Date.now() + ARM_WINDOW;
      armedNote = collectOrderNote();
    }
  }

  Shopify.armCartAdd = function () {
    armedUntil = Date.now() + ARM_WINDOW;
    armedNote = collectOrderNote();
  };

  // Capture phase listeners across click, touch, and pointer events for iOS/Android
  ['click', 'touchstart', 'pointerdown'].forEach(function (evtType) {
    document.addEventListener(evtType, function (e) {
      doArmCart(e.target);
    }, true);
  });

  function themeProperties() {
    var out = [];
    if (Date.now() > armedUntil) return out;
    var nodes = document.querySelectorAll(THEME_PROPERTY_FIELDS);
    var seenKeys = {};

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!el.name || el.disabled) continue;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue;

      // Shopify discards blank properties anyway, so don't send them.
      var value = (el.value == null ? '' : String(el.value)).trim();
      if (!value) continue;

      var key = propertyKey(el.name);
      if (key) seenKeys[key.toLowerCase()] = true;

      out.push({ name: el.name, value: value });
    }

    // Dynamic fallback for custom Size (H x W cm e.g. 210 x 110cm)
    if (!seenKeys['size'] && !seenKeys['custom size']) {
      var sEl = document.getElementById('lk-cx-size-prop');
      var sVal = sEl ? (sEl.value || '').trim() : '';
      if (!sVal) {
        var wEl = document.getElementById('lk-cx-width-prop') ||
                  document.querySelector('input[data-lk-sheet-input="width"]') ||
                  document.querySelector('.option-number-input[data-name*="width" i]') ||
                  document.querySelector('input[name*="width" i]:not([type="hidden"])');
        var hEl = document.getElementById('lk-cx-height-prop') ||
                  document.querySelector('input[data-lk-sheet-input="height"]') ||
                  document.querySelector('.option-number-input[data-name*="height" i]') ||
                  document.querySelector('input[name*="height" i]:not([type="hidden"])');
        var wNum = wEl ? (wEl.value || '').replace(/[^0-9.]/g, '') : '';
        var hNum = hEl ? (hEl.value || '').replace(/[^0-9.]/g, '') : '';
        if (wNum && hNum) {
          sVal = hNum + ' x ' + wNum + 'cm';
        }
      }
      if (sVal) {
        out.push({ name: 'properties[Size]', value: sVal });
        seenKeys['size'] = true;
      }
    }

    // Dynamic fallback for custom Width
    if (!seenKeys['width']) {
      var wEl = document.getElementById('lk-cx-width-prop') ||
                document.querySelector('input[data-lk-sheet-input="width"]') ||
                document.querySelector('.option-number-input[data-name*="width" i]') ||
                document.querySelector('input[name*="width" i]:not([type="hidden"])');
      var wVal = wEl ? (wEl.value || '').trim() : '';
      if (wVal) {
        if (!/cm|in/i.test(wVal)) wVal += ' cm';
        out.push({ name: 'properties[Width]', value: wVal });
        seenKeys['width'] = true;
      }
    }

    // Dynamic fallback for custom Height
    if (!seenKeys['height']) {
      var hEl = document.getElementById('lk-cx-height-prop') ||
                document.querySelector('input[data-lk-sheet-input="height"]') ||
                document.querySelector('.option-number-input[data-name*="height" i]') ||
                document.querySelector('input[name*="height" i]:not([type="hidden"])');
      var hVal = hEl ? (hEl.value || '').trim() : '';
      if (hVal) {
        if (!/cm|in/i.test(hVal)) hVal += ' cm';
        out.push({ name: 'properties[Height]', value: hVal });
        seenKeys['height'] = true;
      }
    }

    // Dynamic fallback for Color
    if (!seenKeys['color'] && !seenKeys['colour']) {
      var cEl = document.getElementById('lk-cx-color-prop') ||
                document.getElementById('lk-sheet-color-val') ||
                document.querySelector('#lk-sheet-colors-list .lk-sheet__color-pill.active') ||
                document.querySelector('.sibling-color-item.active, .sibling-color-item.selected, .color-swatch.active, input[name*="color" i]:checked');
      var cVal = '';
      if (cEl) {
        cVal = cEl.getAttribute('data-color') || cEl.getAttribute('data-value') || cEl.value || cEl.textContent || '';
        cVal = cVal.trim();
      }
      if (cVal) {
        out.push({ name: 'properties[Color]', value: cVal });
        seenKeys['color'] = true;
      }
    }

    // Dynamic fallback for Custom Note
    if (!seenKeys['custom note'] && !seenKeys['note']) {
      var noteEl = document.querySelector('.special-guide-note__textarea, .special-guide-note__input, textarea[name="properties[Custom Note]"], textarea[data-lk-order-note], input[data-lk-order-note]');
      var noteVal = noteEl ? (noteEl.value || '').trim() : '';
      if (noteVal) {
        out.push({ name: 'properties[Custom Note]', value: noteVal });
        seenKeys['custom note'] = true;
      }
    }

    return out;
  }

  // "properties[Custom Note]" -> "Custom Note"
  function propertyKey(name) {
    var match = /^properties\[(.*)\]$/.exec(name);
    return match ? match[1] : null;
  }

  // FormData and URLSearchParams share the has/append interface.
  function appendMissing(bag, props) {
    props.forEach(function (p) {
      if (!bag.has(p.name)) bag.append(p.name, p.value);
    });
    return bag;
  }

  function mergeJson(body, props) {
    var data;
    try {
      data = JSON.parse(body);
    } catch (e) {
      return null;
    }
    if (!data || typeof data !== 'object') return null;

    // /cart/add.js accepts a single item or { items: [...] }.
    var items = Array.isArray(data.items) ? data.items : [data];

    items.forEach(function (item) {
      if (!item || typeof item !== 'object') return;
      if (!item.properties || typeof item.properties !== 'object') item.properties = {};

      props.forEach(function (p) {
        var key = propertyKey(p.name);
        if (key && !(key in item.properties)) item.properties[key] = p.value;
      });
    });

    return JSON.stringify(data);
  }

  function getFallbackVariantId() {
    var activeIdEl = document.querySelector('#AddToCartForm select[name="id"]:not([disabled]) option:checked, #AddToCartForm input[name="id"]:not([disabled]), select[name="id"]:not([disabled]) option:checked, input[name="id"]:not([disabled])');
    if (activeIdEl && activeIdEl.value && activeIdEl.value.trim() !== '' && activeIdEl.value !== 'null' && activeIdEl.value !== 'undefined') {
      return activeIdEl.value.trim();
    }
    var allNodes = document.querySelectorAll('[name="id"]');
    for (var i = 0; i < allNodes.length; i++) {
      var v = (allNodes[i].value || '').trim();
      if (v && /^\d+$/.test(v)) return v;
    }
    return '';
  }

  function sanitizeRequestBody(body, contentType) {
    if (!body) return body;
    var validId = getFallbackVariantId();

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var currentId = body.get('id');
      if ((!currentId || String(currentId).trim() === '' || currentId === 'null' || currentId === 'undefined') && validId) {
        body.set('id', validId);
      }
      return body;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      var currentId = body.get('id');
      if ((!currentId || String(currentId).trim() === '' || currentId === 'null' || currentId === 'undefined') && validId) {
        body.set('id', validId);
      }
      return body;
    }

    if (typeof body === 'string') {
      var looksJson = /json/i.test(contentType || '') || /^\s*[\{\[]/.test(body);
      if (looksJson) {
        try {
          var data = JSON.parse(body);
          if (data && typeof data === 'object') {
            var items = Array.isArray(data.items) ? data.items : [data];
            var modified = false;
            items.forEach(function (item) {
              if (item && typeof item === 'object') {
                if ((!item.id || String(item.id).trim() === '' || item.id === 'null' || item.id === 'undefined') && validId) {
                  item.id = validId;
                  modified = true;
                }
              }
            });
            if (modified) return JSON.stringify(data);
          }
        } catch (e) {}
        return body;
      }

      return Shopify.sanitizeFormData(body, validId);
    }

    return body;
  }

  function withThemeProperties(body, contentType) {
    var sanitized = sanitizeRequestBody(body, contentType);
    var props = themeProperties();
    if (!props.length) return sanitized;

    if (typeof FormData !== 'undefined' && sanitized instanceof FormData) {
      return appendMissing(sanitized, props);
    }
    if (typeof URLSearchParams !== 'undefined' && sanitized instanceof URLSearchParams) {
      return appendMissing(sanitized, props);
    }
    if (typeof sanitized === 'string') {
      var looksJson = /json/i.test(contentType || '') || /^\s*[\{\[]/.test(sanitized);
      if (looksJson) {
        var merged = mergeJson(sanitized, props);
        return merged === null ? sanitized : merged;
      }
      return appendMissing(new URLSearchParams(sanitized), props).toString();
    }

    return sanitized;
  }

  /*
   * Form-encoded multi-item adds double up
   * --------------------------------------------------------------------
   * The Tepo product-options app POSTs /cart/add.js as multipart form
   * data with INDEXED item keys:
   *
   *   items[0][id]=39763285213318
   *   items[0][quantity]=1
   *   items[0][properties[Fabric]]=Polyester      <- its own nesting
   *
   * Shopify only parses an items array reliably from a JSON body. Given a
   * form-encoded one it adds the parsed item AND one extra unit of the
   * same variant with no properties, so ONE click lands TWO lines in the
   * cart: the configured line, plus a bare duplicate. Measured against the
   * live store, items[0][quantity]=3 ends up as quantity 4, and the
   * theme's own merged properties (see THEME_PROPERTY_FIELDS above) landed
   * on the phantom line - which is why the duplicate looked like a
   * half-configured copy of the real one.
   *
   * A flat single-item form post (id / quantity / properties[...]) and a
   * JSON items array both behave correctly, so rewrite the payload into
   * one of those before it leaves the browser. Nothing here changes what
   * is being added - same variant, same quantity, same properties.
   */
  var ITEM_KEY = /^items\[(\d*)\]\[(.+)\]$/;
  var ITEM_SCALARS = { id: true, quantity: true, selling_plan: true };
  // Cart attributes and the note are not per-item, so they ride along.
  var PASSTHROUGH_KEY = /^(attributes\[|note$)/;

  /*
   * "properties][Fabric" (from items[0][properties][Fabric]) and
   * "properties[Fabric]" (the app's own spelling) both mean one property.
   */
  function itemPropertyName(inner) {
    var match = /^properties\]\[(.+)$/.exec(inner) || /^properties\[(.+)\]$/.exec(inner);
    return match ? match[1] : null;
  }

  function bodyPairs(body, contentType) {
    var pairs = [];
    var push = function (value, name) {
      // A File has no place in a cart add and cannot be re-encoded.
      if (typeof value === 'string') pairs.push({ name: name, value: value });
    };

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      body.forEach(push);
      return { kind: 'formdata', pairs: pairs };
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      body.forEach(push);
      return { kind: 'params', pairs: pairs };
    }
    if (typeof body === 'string') {
      var looksJson = /json/i.test(contentType || '') || /^\s*[\{\[]/.test(body);
      if (looksJson) return null;   // already a shape Shopify parses correctly
      new URLSearchParams(body).forEach(push);
      return { kind: 'string', pairs: pairs };
    }
    return null;
  }

  /*
   * Collects items[N][...] pairs into item objects. Returns null when the
   * body carries no items[...] keys at all, or when anything about it is not
   * understood - an unrecognised payload is passed through untouched rather
   * than rewritten into a guess.
   */
  function parseIndexedItems(pairs) {
    var items = [];
    var byIndex = {};
    var autoIndex = 0;
    var sawItems = false;

    for (var i = 0; i < pairs.length; i++) {
      var match = ITEM_KEY.exec(pairs[i].name);
      if (!match) continue;
      sawItems = true;

      var index = match[1];
      var inner = match[2];

      // items[][id] with no index starts a new item each time an id repeats.
      if (index === '') {
        var current = byIndex['auto' + autoIndex];
        if (inner === 'id' && current && current.id) autoIndex++;
        index = 'auto' + autoIndex;
      }

      var item = byIndex[index];
      if (!item) {
        item = byIndex[index] = { properties: {} };
        items.push(item);
      }

      var property = itemPropertyName(inner);
      if (property) {
        // The app sends the theme's confirmation property twice; first wins.
        if (!(property in item.properties)) item.properties[property] = pairs[i].value;
        continue;
      }

      if (ITEM_SCALARS[inner] && !(inner in item)) item[inner] = pairs[i].value;
      // Everything else (form_type, utf8, option-N, product-id, section-id) is
      // product-form scaffolding that /cart/add.js does not need once the
      // variant id is explicit.
    }

    if (!sawItems || !items.length) return null;
    for (var j = 0; j < items.length; j++) {
      if (!items[j].id) return null;
    }
    return items;
  }

  function flatItemPairs(item) {
    var out = [
      { name: 'id', value: String(item.id) },
      { name: 'quantity', value: String(item.quantity || 1) }
    ];
    if (item.selling_plan) out.push({ name: 'selling_plan', value: String(item.selling_plan) });
    Object.keys(item.properties).forEach(function (key) {
      out.push({ name: 'properties[' + key + ']', value: item.properties[key] });
    });
    return out;
  }

  function materialize(kind, pairs) {
    if (kind === 'formdata') {
      var fd = new FormData();
      pairs.forEach(function (p) { fd.append(p.name, p.value); });
      return fd;
    }
    var params = new URLSearchParams();
    pairs.forEach(function (p) { params.append(p.name, p.value); });
    return kind === 'params' ? params : params.toString();
  }

  function jsonItem(item) {
    var out = { id: item.id, quantity: Number(item.quantity || 1) || 1 };
    if (item.selling_plan) out.selling_plan = item.selling_plan;
    if (Object.keys(item.properties).length) out.properties = item.properties;
    return out;
  }

  /*
   * Returns { body, contentType } - contentType is null unless the body had
   * to become JSON, in which case the caller must send it as such.
   */
  function flattenIndexedItems(body, contentType, allowJson) {
    var parsed = bodyPairs(body, contentType);
    if (!parsed) return { body: body, contentType: null };

    var items = parseIndexedItems(parsed.pairs);
    if (!items) return { body: body, contentType: null };

    var extra = parsed.pairs.filter(function (p) { return PASSTHROUGH_KEY.test(p.name); });

    if (items.length === 1) {
      // One item fits the flat form encoding, so the request keeps its
      // original transport and content type.
      return { body: materialize(parsed.kind, flatItemPairs(items[0]).concat(extra)), contentType: null };
    }

    // Several items can only be expressed as a JSON items array. An XHR
    // cannot have its Content-Type replaced (setRequestHeader appends rather
    // than overwrites), so a multi-item XHR is left alone - no app on this
    // store is known to send one.
    if (!allowJson) return { body: body, contentType: null };

    var payload = { items: items.map(jsonItem) };
    extra.forEach(function (p) {
      var attr = /^attributes\[(.+)\]$/.exec(p.name);
      if (attr) {
        payload.attributes = payload.attributes || {};
        payload.attributes[attr[1]] = p.value;
      } else if (p.name === 'note') {
        payload.note = p.value;
      }
    });
    return { body: JSON.stringify(payload), contentType: 'application/json' };
  }

  // Rewrite the payload shape first, then merge the theme's own properties
  // into it - that way they land on the real line and not on a phantom one.
  function prepareAddBody(body, contentType, allowJson) {
    var flat = flattenIndexedItems(body, contentType, allowJson);
    return {
      body: withThemeProperties(flat.body, flat.contentType || contentType),
      contentType: flat.contentType
    };
  }

  function withContentType(headers, type) {
    if (typeof Headers === 'function') {
      var merged = new Headers(headers || {});
      merged.set('Content-Type', type);
      return merged;
    }
    var out = {};
    if (headers) Object.keys(headers).forEach(function (k) { out[k] = headers[k]; });
    out['Content-Type'] = type;
    return out;
  }

  /*
   * Both transports finish an add the same way: save the note first, then
   * refresh the UI, then honour a pending Buy It Now redirect. The redirect
   * waits on the note write, otherwise checkout can load before cart.note
   * has been set and the note is lost.
   */
  function completeCartMutation(url) {
    invalidateCartCache();

    /*
     * Tell the theme's buttons the add really landed. The options app can
     * spend ten seconds minting a variant through its own proxy before it
     * POSTs /cart/add.js, which is far longer than any timer the button
     * could reasonably guess at, so the button waits for this instead of
     * counting seconds. See snippets/product-cart-action.liquid.
     */
    if (isCartAdd(url)) {
      try {
        document.dispatchEvent(new CustomEvent('lk:cart:add'));
      } catch (e) {}
    }

    var isAdd = isCartAdd(url);

    if (isAdd && window._redirectToCheckoutOnAdd) {
      window._redirectToCheckoutOnAdd = false;
      window._redirectToCartOnAdd = false;
      window._lkLastAddedVariantId = null;
      window._lkLastAddedQty = null;
      // Save the note, trim the cart to the item just added, then go to checkout
      Shopify.lkCheckoutNow(syncOrderNote());
      return;
    }

    if (isAdd && window._redirectToCartOnAdd) {
      window._redirectToCartOnAdd = false;
      window._redirectToCheckoutOnAdd = false;
      window._lkLastAddedVariantId = null;
      window._lkLastAddedQty = null;
      if (isAdd) syncOrderNote();
      window.location.href = '/cart';
      return;
    }

    var done = isAdd ? syncOrderNote() : Promise.resolve();

    done.then(function () {
      Shopify.refreshCartUI();
    });
  }

  function headerValue(headers, name) {
    if (!headers) return '';
    if (typeof headers.get === 'function') return headers.get(name) || '';
    return headers[name] || headers[name.toLowerCase()] || '';
  }

  /*
   * Writes the snapshotted note onto cart.note. Uses the fetch captured
   * before we patch window.fetch, so these two requests do not re-enter the
   * interceptor and re-trigger the refresh/checkout-redirect logic.
   *
   * Resolves either way - a failed note write must never block the add or
   * the Buy It Now redirect.
   */
  var rawFetch = window.fetch ? window.fetch.bind(window) : null;

  function syncOrderNote() {
    var text = (armedNote || collectOrderNote()).trim();
    armedNote = '';

    if (!text || !rawFetch) return Promise.resolve();

    return rawFetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({ note: text })
    }).catch(function () { return null; });
  }

  // Exposed so a caller that does its own post-add redirect (Buy It Now in
  // snippets/product-cart-action.liquid) can wait for the note to be saved.
  // armedNote is consumed on the first call, so a second one is a no-op.
  Shopify.syncOrderNote = syncOrderNote;

  /*
   * Buy It Now checks out ONLY the item just added
   * --------------------------------------------------------------------
   * Shopify has one cart per visitor, and /checkout always checks out all of
   * it. Buy It Now used to add the item and redirect, so anything the visitor
   * had already put in the bag (or left there from an earlier visit) came
   * along into the order.
   *
   * Before the add, the cart is snapshotted as { line key: quantity }. After
   * the add lands, every line is trimmed to just what the add contributed:
   * lines that existed before go to 0, a line the add merged into keeps only
   * the added units, and new lines (custom variants minted by the options
   * app, with their properties) are kept as they are. Any failure falls back
   * to the old behaviour - checkout of the whole cart - and never blocks.
   */
  var buyNowBefore = null;

  function readCartNoCache() {
    return rawFetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    }).then(function (res) { return res.json(); });
  }

  Shopify.lkSnapshotCart = function () {
    buyNowBefore = null;
    if (!rawFetch) return Promise.resolve();
    return readCartNoCache().then(function (cart) {
      var before = {};
      (cart.items || []).forEach(function (item) { before[item.key] = item.quantity; });
      buyNowBefore = before;
    }).catch(function () { buyNowBefore = null; });
  };

  function isolateBuyNowItem() {
    var before = buyNowBefore;
    buyNowBefore = null;
    if (!before || !rawFetch) return Promise.resolve();

    return readCartNoCache().then(function (cart) {
      var updates = {};
      var changed = false;

      (cart.items || []).forEach(function (item) {
        var added = item.quantity - (before[item.key] || 0);
        var keep = added > 0 ? added : 0;
        if (keep !== item.quantity) {
          updates[item.key] = keep;
          changed = true;
        }
      });

      if (!changed) return null;

      return rawFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates: updates })
      });
    }).catch(function () { return null; });
  }

  // The single way Buy It Now reaches checkout. `after` is any promise that
  // must settle first (the order-note write).
  /*
   * Razorpay Magic Checkout is the store's checkout. Magic hooks real
   * checkout buttons (button[name=checkout]) and opens its modal; it does
   * NOT see a script doing window.location = '/checkout'. So every checkout
   * entry point clicks a genuine checkout button instead of navigating.
   * The button sits in a <form action="/cart">, so if Magic ever fails to
   * load the browser still falls through to Shopify's own checkout.
   */
  Shopify.lkOpenCheckout = function () {
    var form = document.getElementById('lk-magic-checkout-form');
    if (!form) {
      form = document.createElement('form');
      form.id = 'lk-magic-checkout-form';
      form.method = 'post';
      form.action = '/cart';
      form.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
      var btn = document.createElement('button');
      btn.type = 'submit';
      btn.name = 'checkout';
      btn.className = 'checkout_btn';
      btn.setAttribute('aria-hidden', 'true');
      btn.tabIndex = -1;
      form.appendChild(btn);
      document.body.appendChild(form);
    }
    form.querySelector('button').click();
  };

  Shopify.lkCheckoutNow = function (after) {
    if (window._lkCheckoutGoing) return;
    window._lkCheckoutGoing = true;
    Promise.resolve(after).catch(function () {}).then(isolateBuyNowItem).then(function () {
      Shopify.lkOpenCheckout();
      // Magic opens a modal on this page (no navigation), so release the guard
      setTimeout(function () { window._lkCheckoutGoing = false; }, 1500);
    });
  };

  // Coming back with the browser's Back button must not leave Buy It Now stuck.
  window.addEventListener('pageshow', function () { window._lkCheckoutGoing = false; });

  // --- fetch ---
  if (window.fetch) {
    var nativeFetch = rawFetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url ? input.url : '');
      var args = arguments;

      if (isCacheableCartRead(input, init)) {
        try {
          if (cartCache && (Date.now() - cartCache.at) < CART_CACHE_TTL) {
            return Promise.resolve(cachedCartResponse());
          }
          // Several pollers can tick together; let them share one request.
          if (cartInFlight) return cartInFlight.then(function () {
            return cartCache ? cachedCartResponse() : nativeFetch.apply(window, args);
          });

          cartInFlight = nativeFetch.apply(window, args).then(function (response) {
            cartInFlight = null;
            if (response && response.ok) {
              return response.clone().text().then(function (text) {
                cartCache = { at: Date.now(), body: text };
                return response;
              }, function () { return response; });
            }
            return response;
          }, function (err) {
            cartInFlight = null;
            throw err;
          });

          return cartInFlight;
        } catch (e) {
          cartInFlight = null;
        }
      }

      // A Request object carries its body as a stream that cannot be
      // rewritten synchronously, so only the (url, init) form is merged.
      if (isCartAdd(url) && init && init.body != null) {
        try {
          var prepared = prepareAddBody(init.body, headerValue(init.headers, 'Content-Type'), true);
          if (prepared.body !== init.body) {
            var nextInit = Object.assign({}, init, { body: prepared.body });
            if (prepared.contentType) {
              nextInit.headers = withContentType(init.headers, prepared.contentType);
            }
            args = [input, nextInit];
          }
        } catch (e) {}
      }

      return nativeFetch.apply(window, args).then(function (response) {
        try {
          if (response && response.ok && isCartMutation(url)) {
            if (isCartAdd(url)) {
              // IMPORTANT: parse response FIRST so _lkLastAddedVariantId is set
              // before completeCartMutation fires its redirect. Without this the
              // redirect races the .json() and sees a null variant, falling back
              // to /checkout which includes the entire shared cart.
              response.clone().json().then(function (data) {
                try {
                  var items = (data && data.items) ? data.items : (data && data.id ? [data] : []);
                  if (items.length > 0) {
                    window._lkLastAddedVariantId = items[0].variant_id;
                    window._lkLastAddedQty       = items[0].quantity || 1;
                  }
                } catch (_) {}
                completeCartMutation(url);
              }).catch(function () {
                // Parse failed — still fire the mutation (redirect will fall back to /checkout)
                completeCartMutation(url);
              });
            } else {
              completeCartMutation(url);
            }
          }
        } catch (e) {}
        return response;
      });
    };
  }

  // --- XMLHttpRequest (jQuery.ajax) ---
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  var nativeSetHeader = XMLHttpRequest.prototype.setRequestHeader;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    if (String(name).toLowerCase() === 'content-type') this.__cartContentType = value;
    return nativeSetHeader.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    var xhr = this;
    var args = arguments;

    if (isCartAdd(xhr.__cartUrl) && body != null) {
      try {
        // allowJson is false here: an XHR's Content-Type cannot be replaced.
        var merged = prepareAddBody(body, xhr.__cartContentType, false).body;
        if (merged !== body) args = [merged];
      } catch (e) {}
    }

    xhr.addEventListener('load', function () {
      try {
        if (xhr.status >= 200 && xhr.status < 300 && isCartMutation(xhr.__cartUrl)) {
          // Capture variant from XHR response (OPC apps typically use jQuery.ajax / XHR)
          if (isCartAdd(xhr.__cartUrl)) {
            try {
              var data = JSON.parse(xhr.responseText);
              var items = (data && data.items) ? data.items : (data && data.id ? [data] : []);
              if (items.length > 0) {
                window._lkLastAddedVariantId = items[0].variant_id;
                window._lkLastAddedQty       = items[0].quantity || 1;
              }
            } catch (_) {}
          }
          completeCartMutation(xhr.__cartUrl);
        }
      } catch (e) {}
    });

    return nativeSend.apply(this, args);
  };
})();

Shopify.onItemAdded = function(line_item) {
  // The cart-mutation watcher above already queues exactly one refresh.
  // Calling Shopify.getCart() here as well produced a second, sequential
  // round trip that only started after /cart/add.js had finished.
  Shopify.refreshCartUI();
};

Shopify.onCartUpdate = function(cart) {
  Shopify.cartUpdateInfo(cart, '.single-cart-item-loop');
};

Shopify.onError = function(XMLHttpRequest, textStatus) {
  Shopify.isAddingToCart = false;
  jQuery('.btn--loading').removeClass('btn--loading');
  jQuery('.cart-btn-spinner, .buynow-btn-spinner').hide();
  jQuery('#AddToCartText, #BuyNowText').show();

  var data = null;
  try {
    data = JSON.parse(XMLHttpRequest.responseText);
  } catch (e) {
    data = null;
  }

  var str;
  if (data && !!data.message) {
    str = data.description;
  } else if (data) {
    str = 'Error : ' + Shopify.fullMessagesFromErrors(data).join('; ') + '.';
  } else {
    str = 'Sorry, we could not add that to your cart. Please try again.';
  }

  jQuery('.error_message').text(str);
  jQuery('#modalAddToCartError').modal("toggle");
  setTimeout(function () {
  	jQuery('.shopping__cart').removeClass("shopping__cart__on");
    jQuery('.body__overlay').removeClass("is-visible");
  },2000);
}

Shopify.addItem = function(variant_id, quantity, callback) {
  if (Shopify.isAddingToCart) return;
  Shopify.isAddingToCart = true;

  var quantity = quantity || 1;
  var params = {
    type: 'POST',
    url: '/cart/add.js',
    data: 'quantity=' + quantity + '&id=' + variant_id,
    dataType: 'json',
    success: function(line_item) {
      Shopify.isAddingToCart = false;
      if ((typeof callback) === 'function') {
        callback(line_item);
      }
      else {
        Shopify.cartPopap(variant_id);
        Shopify.onItemAdded(line_item);
      }
    },
    error: function(XMLHttpRequest, textStatus) {
      Shopify.isAddingToCart = false;
      Shopify.onError(XMLHttpRequest, textStatus);
    }
  };
  jQuery.ajax(params);
};

Shopify.isAddingToCart = false;

Shopify.sanitizeFormData = function (rawData, fallbackVariantId) {
  if (!rawData) rawData = '';
  var paramsArr = rawData.split('&');
  var cleanParams = [];
  var foundId = false;

  for (var i = 0; i < paramsArr.length; i++) {
    if (!paramsArr[i]) continue;
    var pair = paramsArr[i].split('=');
    var key = decodeURIComponent(pair[0]);
    var val = pair[1] ? decodeURIComponent(pair[1]).trim() : '';

    if (key === 'id') {
      if (val && !foundId && val !== 'null' && val !== 'undefined') {
        cleanParams.push('id=' + encodeURIComponent(val));
        foundId = true;
      }
    } else {
      cleanParams.push(paramsArr[i]);
    }
  }

  if (!foundId) {
    var activeIdEl = document.querySelector('#AddToCartForm select[name="id"]:not([disabled]) option:checked, #AddToCartForm input[name="id"]:not([disabled])') ||
                     document.querySelector('select[name="id"]:not([disabled]) option:checked, input[name="id"]:not([disabled])');
    var idValue = (activeIdEl && activeIdEl.value && activeIdEl.value.trim() !== '') 
                  ? activeIdEl.value.trim() 
                  : (fallbackVariantId || '');
    if (idValue) {
      cleanParams.push('id=' + encodeURIComponent(idValue));
    }
  }

  return cleanParams.join('&');
};

Shopify.addItemFromForm = function(form_id, variant_id, callback) {
    if (Shopify.isAddingToCart) return;
    Shopify.isAddingToCart = true;

    var $form = jQuery('#' + form_id);
    if (!$form.length) {
      $form = jQuery('form[action*="/cart/add"], form.product-content-inner, form.shopify-product-form');
    }
    var $btn = jQuery('#AddToCart');
    if ($btn.length) {
      $btn.prop('disabled', true).addClass('btn--loading');
      $btn.find('#AddToCartText').hide();
      $btn.find('.cart-btn-spinner').css('display', 'inline-flex');
    }

    var $inputs = $form.find('input, select, textarea').not('.product-complementary *, .complementary_releted_product_area *, .product-grid-item *');
    var rawSerialized = $inputs.length ? $inputs.serialize() : $form.serialize();
    var serializedData = Shopify.sanitizeFormData(rawSerialized, variant_id);

    var params = {
      type: 'POST',
      url: '/cart/add.js',
      beforeSend: function(){
        if(form_id == "add-item-qv") {
          jQuery('#' + form_id).find(".addtocartqv").html(jQuery(".quickViewModal_info .button_wait").html());
        }
      },
      data: serializedData,
      dataType: 'json',
      success: function(line_item) {
        Shopify.isAddingToCart = false;
        if ($btn.length) {
          $btn.prop('disabled', false).removeClass('btn--loading');
          $btn.find('.cart-btn-spinner').hide();
          $btn.find('#AddToCartText').show();
        }
        if ((typeof callback) === 'function') {
          callback(line_item);
        }
        else {
          if(form_id != "add-item-qv") {
            Shopify.cartPopapForm(variant_id);
          } else {
          	jQuery('#' + form_id).find(".addtocartqv").html(jQuery(".quickViewModal_info .button_added").html());
            jQuery('#' + form_id).find(".addtocartqv").addClass("added_in_cart");
            setTimeout(function(){
              	jQuery('#' + form_id).find(".addtocartqv").removeClass("added_in_cart");
            	jQuery('#' + form_id).find(".addtocartqv").html(jQuery(".quickViewModal_info .button").html());
            }, 2000);
          }
          Shopify.onItemAdded(line_item);
        }
      },
      error: function(XMLHttpRequest, textStatus) {
        Shopify.isAddingToCart = false;
        if ($btn.length) {
          $btn.prop('disabled', false).removeClass('btn--loading');
          $btn.find('.cart-btn-spinner').hide();
          $btn.find('#AddToCartText').show();
        }
        if(form_id != "add-item-qv") {
          Shopify.onError(XMLHttpRequest, textStatus);
        } else {
          jQuery('#' + form_id).find(".addtocartqv").html(jQuery(".quickViewModal_info .button_error").html());
          jQuery('#' + form_id).find(".addtocartqv").addClass("error_in_cart");
          setTimeout(function(){
            jQuery('#' + form_id).find(".addtocartqv").removeClass("error_in_cart");
            jQuery('#' + form_id).find(".addtocartqv").html(jQuery(".quickViewModal_info .button").html());
          }, 2000);
        }
      }
    };
    jQuery.ajax(params);
};



/* user functions */

Shopify.addItemFromFormStart = function(form, product_id) {
  Shopify.addItemFromForm(form, product_id);
}

Shopify.cartPopap = function(variant_id) {
    // Variant ids are numeric, and a CSS class selector may not start with a
    // digit — ".39763285213318" is invalid. Match the class attribute instead.
    var $row = jQuery('[class~="' + variant_id + '"]').first();
    var image = $row.find('.popup_cart_image').attr("src");
    var title = $row.find('.popup_cart_title').text();
    var price = $row.find('.popup_cart_price').first().text();
    if (typeof window.openCartAddedPopup === 'function') {
      window.openCartAddedPopup({ img: image, title: title, price: price ? price.trim() : '' });
    } else {
      jQuery('.popupimage').attr("src", image || '');
      jQuery('.productmsg').text(title || '');
      jQuery('#modalAddToCart').modal("toggle");
    }
}
Shopify.cartPopapForm = function(variant_id) {
  	var image = jQuery('.product_variant_image').attr("src");
  	var title = jQuery('#popup_cart_title').text();
  	var price = jQuery('#ProductPrice').first().text();
    if (typeof window.openCartAddedPopup === 'function') {
      window.openCartAddedPopup({ img: image, title: title, price: price ? price.trim() : '' });
    } else {
      jQuery('.popupimage').attr("src", image || '');
      jQuery('.productmsg').text(title || '');
      jQuery('#modalAddToCart').modal("toggle");
    }
}


// Escape values that get concatenated into the mini-cart markup, so a product
// title containing a quote or angle bracket cannot break the row.
Shopify.escapeHtml = function (value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};


Shopify.cartUpdateInfo = function(cart, cart_cell_id) {
  var formatMoney = window.money_format;

  if ((typeof cart_cell_id) === 'string') {
    var cart_cell = jQuery(cart_cell_id);

    if (cart_cell.length) {
      var items = (cart && cart.items) ? cart.items : [];

      if (items.length) {
        if (items.length == 1) {
          jQuery(".single-product-cart, .item-single-item-count").css({"display": "block"});
          jQuery(".cart-empty-title, .item-multiple-item-count").css({"display": "none"});
        } else {
          jQuery(".single-product-cart, .item-multiple-item-count").css({"display": "block"});
          jQuery(".cart-empty-title,  .item-single-item-count").css({"display": "none"});
        }

        // Build the rows as one string and insert once, instead of up to 20
        // separate .appendTo() calls (20 layout/paint passes per cart update).
        var html = '';
        for (var i = 0; i < items.length && i < 20; i++) {
          var item = items[i];
          var url = Shopify.escapeHtml(item.url);
          var img = Shopify.escapeHtml(item.image);
          var title = Shopify.escapeHtml(item.title);

          var propsHtml = '';
          if (item.properties && typeof item.properties === 'object') {
            Object.keys(item.properties).forEach(function (propKey) {
              var val = item.properties[propKey];
              if (val != null && String(val).trim() !== '' && propKey.charAt(0) !== '_') {
                propsHtml += '<span class="product-property d-block" style="font-size: 11px; color: #64748b; line-height: 1.3;">'
                  + Shopify.escapeHtml(propKey) + ': ' + Shopify.escapeHtml(val) + '</span>';
              }
            });
          }

          html += '<div class="single-cart-box">'
                +   '<div class="cart-img">'
                +     '<a href="' + url + '"><img src="' + img + '" alt=""></a>'
                +     '<span class="pro-quantity">' + item.quantity + 'x</span>'
                +   '</div>'
                +   '<div class="cart-content">'
                +     '<h6><a href="' + url + '">' + title + '</a></h6>'
                +     propsHtml
                +     '<span class="cart-price">' + Shopify.formatMoney(item.price, formatMoney) + '</span>'
                +   '</div>'
                +   '<a class="del-icone" href="javascript:void(0);" onclick="Shopify.removeItem(' + item.variant_id + ')"><i class="far fa-times"></i></a>'
                + '</div>';
        }
        cart_cell.html(html);
      } else {
        jQuery(".single-product-cart, .item-single-item-count").css({"display": "none"});
        jQuery(".cart-empty-title").css({"display": "block"});
        cart_cell.empty();
      }
    }
  }

  changeHtmlValue(".shopping-cart__total", Shopify.formatMoney(cart.total_price, formatMoney));
  changeHtmlValue(".bigcounter", cart.item_count);

  // Only poke the currency switcher when one is actually on the page —
  // this used to fire on every single cart update.
  var $currency = jQuery('.currency .active');
  if ($currency.length) {
    $currency.trigger('click');
  }
}

//Utils
function changeHtmlValue (cell, value) {
  var $cartLinkText = jQuery(cell);
  $cartLinkText.html(value);
  var num = parseInt(value, 10);
  if (!isNaN(num) && num > 0) {
    $cartLinkText.addClass('has-items');
  } else {
    $cartLinkText.removeClass('has-items');
  }
};
